import { IconButton, useEventCallback } from '@mui/material';
import { headCellsDocumentCivilUnion } from 'civil-status/constants/head-cells';
import { useFormik } from 'formik';
import { useCallback, useMemo, useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import Table from 'shell/components/shared/table';
import { noop } from 'shell/utils/noop';
import { DocumentListCivilUnionCrumbs } from 'civil-status/constants/breadcrumb';
import {
  DocumentCivilUnionFilterForm,
  DocumentCivilUnionVM,
} from 'civil-status/view-models/document-civil-union-list';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponseList } from 'shell/models/http-response';
import { __CIVIL_STATUS_API_URL__ } from 'shell/constants/environment';
import { Delete, Visibility } from '@mui/icons-material';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { DocumentCivilUnionList } from 'shell/models/document-civil-union';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import PmiTooltipError from 'shell/components/shared/tooltip-error';
import { AlertModal } from 'system/components/shared/delete-alert/types';
import DeleteAlertModal from 'system/components/shared/delete-alert';
import DeleteSuccessModal from 'system/components/shared/delete-success';
import ListHeader from 'shell/components/shared/list-header';

export default function DocumentCivilUnionListPage(): React.ReactElement {
  const { userProfile } = useUserProfile();
  const [deleteAlertModal, setDeleteAlertModal] = useState<AlertModal<DocumentCivilUnionVM>>({
    element: null,
    open: false,
  });
  const [deleteSuccessModal, setDeleteSuccessModal] = useState<AlertModal<DocumentCivilUnionVM>>({
    element: null,
    open: false,
  });
  const [civilUnionList, getCivilUnionList] = useHttpFetch<
    HttpResponseList<DocumentCivilUnionList>,
    Array<DocumentCivilUnionVM>
  >(`${__CIVIL_STATUS_API_URL__}/atti-unione-civile-sima`, {
    onBootstrap: true,
    options: {
      errorCatching: true,
      method: 'GET',
      loading: true,
    },
    transformer: (response) => {
      return response.data?.payload?.list.map((item) => {
        const itemResult: DocumentCivilUnionVM = {
          code: item.matricolaConiuge1?.toString() || '',
          surname: item.cognomeConiuge1 || '',
          name: item.nomeConiuge1 || '',
          year: item.key?.anno || '',
          code2: item.matricolaConiuge2?.toString() || '',
          surname2: item.cognomeConiuge2 || '',
          name2: item.nomeConiuge2 || '',
          documentNumber: item.key?.numeroAtto?.toString() || '',
          numReg: item.key?.numeroRegistro || '',
          parte: item.key?.parte || '',
          serie: item.key?.serie2 !== ' ' ? `${item.key?.serie1}${item.key?.serie2}` : '',
          user: item.userLock?.trim() || '',
          locked: !item.userLock?.trim()
            ? false
            : item.userLock?.trim() !== userProfile.datiLogin?.key?.usernameEsteso,
          key: item.key,
        };
        return itemResult;
      });
    },
  });

  const lockDocument = useHttpClient(
    `${__CIVIL_STATUS_API_URL__}/atti-unione-civile-sima/blocca-atto`,
    {
      options: {
        errorCatching: true,
        method: 'PUT',
        loading: true,
      },
    }
  );

  const updateFlagCivilUnionDocument = useHttpClient(
    `${__CIVIL_STATUS_API_URL__}/atti-unione-civile-sima/aggiornamento-indicatore-caricamento`,
    {
      options: {
        errorCatching: true,
        method: 'PUT',
        loading: true,
      },
    }
  );

  const data = civilUnionList.data as Array<DocumentCivilUnionVM>;

  const formikForm = useFormik<DocumentCivilUnionFilterForm>({
    initialValues: {
      page: 0,
      rowsPerPage: 10,
      orders: {},
    },
    onSubmit: noop,
  });

  // HTTP Request to get list
  const navigate = useNavigate();

  const onChangePage = useCallback(
    (page) => {
      formikForm.setFieldValue('page', page || 0);
    },
    [formikForm.setFieldValue]
  );

  const onChangeRowsPerPage = useCallback(
    (rowsPerPage) => {
      const newDataset = {
        ...formikForm.values,
        rowsPerPage: rowsPerPage || 0,
        page: 0,
      };

      formikForm.setValues(newDataset);
    },
    [formikForm.setValues, formikForm.values]
  );

  const onVisibilityPress = useCallback(
    (cell): Promise<DocumentCivilUnionVM> => {
      return new Promise((resolve) => {
        (
          lockDocument({
            data: {
              key: cell.key,
            },
          }) as ResponsePromise<void>
        ).then(() => {
          navigate({
            pathname: generatePath(
              '/stato-civile/unione-civile/carica/:anno/:numeroRegistro/:numeroAtto/:parte/:serie1/:serie2',
              {
                ...(cell.key || {}),
                parte: cell.key?.parte === ' ' ? 'null' : cell.key.parte,
                serie1: cell.key?.serie1 === ' ' ? 'null' : cell.key.serie1,
                serie2: cell.key?.serie2 === ' ' ? 'null' : cell.key.serie2,
              }
            ),
          });
          resolve(cell);
        });
      });
    },
    [navigate, lockDocument]
  );

  const deleteCivilUnionDocument = useEventCallback((civilUnionDocument: DocumentCivilUnionVM) => {
    setDeleteAlertModal({ element: civilUnionDocument, open: true });
  });

  /**
   * The list of document act.
   */
  const list = useMemo(() => {
    return (
      data?.map((item) => ({
        ...item,
        action: (
          <>
            <PmiTooltipError
              show={item.locked}
              message={item.locked ? `Atto bloccato dall'utente ${item.user}` : ''}
            >
              <IconButton
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onVisibilityPress(item);
                }}
                disabled={item.locked}
              >
                <Visibility />
              </IconButton>
            </PmiTooltipError>
            <span>
              <IconButton
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  deleteCivilUnionDocument(item);
                }}
                color="cdmBrand"
              >
                <Delete />
              </IconButton>
            </span>
          </>
        ),
      })) || []
    );
  }, [data, onVisibilityPress, deleteCivilUnionDocument]);

  return (
    <>
      <Breadcrumbs items={DocumentListCivilUnionCrumbs} base="stato-civile" />
      <ListHeader
        addBtnAriaLabel="Aggiungi"
        addBtnLabel="Aggiungi"
        canAdd
        onAddBtnClick={() => {
          navigate('/stato-civile/unione-civile/carica/nuovo');
        }}
        title="Lista Atti Unione Civile - Caricamento"
      />
      {list.length > 0 ? (
        <Table
          ariaLabel="Lista atti Unione civile"
          page={formikForm.values.page}
          rowsPerPage={formikForm.values.rowsPerPage}
          body={list}
          count={list.length || 0}
          head={headCellsDocumentCivilUnion}
          onSort={noop}
          onPageChange={onChangePage}
          onRowsPerPageChange={onChangeRowsPerPage}
          onVisibilityPress={noop}
          onEditPress={noop}
          onTrashPress={noop}
          orders={formikForm.values.orders}
          isNotLayoutFixed
          noSort
          hideButtons
        />
      ) : undefined}
      <DeleteAlertModal
        title="Cancellazione Atto/Unione Civile"
        message="Sei sicuro di considerare l'Atto caricato ed eliminarlo dalla lista?"
        open={deleteAlertModal.open}
        onClose={() => setDeleteAlertModal({ open: false, element: null })}
        onYes={() => {
          (
            updateFlagCivilUnionDocument({
              data: {
                anno: deleteAlertModal.element?.year || undefined,
                cognome1: deleteAlertModal.element?.surname || undefined,
                cognome2: deleteAlertModal.element?.surname2 || undefined,
                matricola1: deleteAlertModal.element?.code || undefined,
                matricola2: deleteAlertModal.element?.code2 || undefined,
                nome1: deleteAlertModal.element?.name || undefined,
                nome2: deleteAlertModal.element?.name2 || undefined,
                numeroAtto: deleteAlertModal.element?.documentNumber || undefined,
                numeroRegistro: deleteAlertModal.element?.numReg || undefined,
                parte: deleteAlertModal.element?.parte || undefined,
                serie: deleteAlertModal.element?.serie || undefined,
              },
            }) as ResponsePromise<void>
          ).then(() => {
            setDeleteAlertModal({ open: false, element: null });
            setDeleteSuccessModal({ open: true, element: deleteAlertModal.element });
          });
        }}
        onNo={() => setDeleteAlertModal({ open: false, element: null })}
      />
      <DeleteSuccessModal
        open={deleteSuccessModal.open}
        title="Cancellazione Atto/Unione Civile"
        message={`La cancellazione dell'Atto Unione Civile è avvenuta con successo.`}
        onClose={() => {
          getCivilUnionList();
          setDeleteSuccessModal({ open: false, element: null });
        }}
        onOk={() => {
          getCivilUnionList();
          setDeleteSuccessModal({ open: false, element: null });
        }}
      />
    </>
  );
}

DocumentCivilUnionListPage.displayName = 'home page component';
